import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import SearchablePlateProcedureParameterDropdown from 'samples/plate-maps/display-layer-controls/SearchablePlateProcedureParameterDropdown';
import {PlateMaterialInput} from 'samples/plate-maps/PlateMapConstants';
import {NO_MATERIAL_INPUT_SENTINEL} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {
  getWrongSchemaMaterialInputErrorMessage,
  isValidSchemaForMaterialInput,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {RequiredLinkTagSchema} from 'schemas/fields/SchemaFieldConstants';
import {ORANGE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {OptionData} from 'ui-library/menus/MenuConstants';
import OptionContent from 'ui-library/menus/OptionContent';
import PopoverIcon from 'ui-library/PopoverIcon';

export interface MaterialInputSelectorProps {
  typedValue: TypedValue;
  locked: boolean;
  onUpdate: (typedValue: TypedValue) => void;
  plateMaterialInputs: Array<PlateMaterialInput>;
  schemaId: string;
}

function renderOptionContent(optionData: OptionData): JSX.Element {
  let children: React.ReactNode;
  if (optionData.disabled) {
    children = (
      <span className={css(styles.node)}>
        <span>{optionData.name}</span>
        <PopoverIcon
          className={css(styles.icon)}
          icon="exclamation-triangle"
          renderContent={() => optionData.disabledMessage}
        />
      </span>
    );
  } else {
    children = optionData.name;
  }
  return <OptionContent>{children}</OptionContent>;
}

export default function MaterialInputSelector({
  typedValue,
  locked,
  onUpdate,
  plateMaterialInputs,
  schemaId,
}: MaterialInputSelectorProps): JSX.Element | null {
  const materialInputsById = useMemo(
    () =>
      objectFromEntries(
        plateMaterialInputs.map<[string, PlateMaterialInput]>((plateMaterialInput) => [
          plateMaterialInput.versionId,
          plateMaterialInput,
        ])
      ),
    [plateMaterialInputs]
  );

  if (typedValue.type !== BaseTypedValueType.CLIENT_IDENTIFIABLE) {
    return null;
  }
  const staticDisplay = formatTypedValue(typedValue);

  if (locked) {
    return <span>{staticDisplay}</span>;
  }

  return (
    <SearchablePlateProcedureParameterDropdown<PlateMaterialInput>
      parameters={plateMaterialInputs}
      closeOnInsideClick={true}
      onSelect={(selectedValues) => {
        // Since the searchable select dropdown only supports an array of selected values, only select the
        // last (most recently clicked) value to ensure only one is selected.
        const materialInputId = selectedValues.slice(-1)[0];
        if (materialInputId == null) {
          onUpdate({
            type: BaseTypedValueType.CLIENT_IDENTIFIABLE,
            id: NO_MATERIAL_INPUT_SENTINEL,
            name: '', // Empty cell value is calculated in FillPlateTransferTableUtil.getSharedRow
          });
        } else {
          onUpdate({
            type: BaseTypedValueType.CLIENT_IDENTIFIABLE,
            name: materialInputsById[materialInputId].name,
            id: materialInputId,
          });
        }
      }}
      selectedValues={[typedValue.id]}
      disabled={false}
      getOptionData={(materialInput) => {
        const optionData: OptionData = {
          name: materialInput.name,
          value: materialInput.versionId,
          dataTestAttrs: {
            component: 'MaterialInputSelector',
            element: 'item',
            key: materialInput.versionId,
          },
        };
        const {requiredLink} = materialInput.schemaField;
        if (!isValidSchemaForMaterialInput(materialInput, schemaId)) {
          optionData.disabled = true;
          optionData.disabledMessage = getWrongSchemaMaterialInputErrorMessage(
            (requiredLink as RequiredLinkTagSchema).tagSchema!.name
          );
        }
        return optionData;
      }}
      noneSelectedMessage=""
      dropdownToggleOverride={
        <div className={css(styles.toggle)}>
          <span className={css(styles.toggleText)}>{staticDisplay}</span>
          <BenchlingIcon className={css(styles.toggleIcon)} icon="chevron-down" />
        </div>
      }
      renderOptionContent={renderOptionContent}
    />
  );
}

const styles = StyleSheet.create({
  node: {display: 'flex', justifyContent: 'space-between'},
  icon: {color: ORANGE},
  toggle: {display: 'flex', cursor: 'pointer'},
  toggleText: {flexGrow: 1, overflow: 'hidden', marginRight: 14},
  toggleIcon: {alignSelf: 'center'},
});
